// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Monitor Management
// Manage disposal site monitors, certifications, and assignments
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Stack, TextInput,
  Select, SimpleGrid, Modal, ActionIcon, Textarea,
} from '@mantine/core';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
  IconShieldCheck, IconUser,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useDebrisStore } from '../../store/debrisStore';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { createRecord, updateRecord, deleteRecord, fetchTable, TABLES, FIELDS } from '../../lib/api-switch';
import { formatPhone } from '../../lib/phoneFormat';

interface Monitor {
  id: number;
  Rid: number;
  incidentId: string;
  fullName: string;
  company: string;
  email: string;
  phone: string;
  role: string;
  assignedSites: string;
  assignedZones: string;
  certifications: string;
  status: string;
  notes: string;
}

const EMPTY: Partial<Monitor> = {
  fullName: '', company: '', email: '', phone: '', role: 'site_monitor',
  assignedSites: '', assignedZones: '', certifications: '', status: 'active', notes: '',
};

const ROLE_OPTIONS = [
  { value: 'site_monitor', label: 'Site Monitor' },
  { value: 'field_verifier', label: 'Field Verifier' },
  { value: 'lead_monitor', label: 'Lead Monitor' },
  { value: 'qc_inspector', label: 'QC Inspector' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'standby', label: 'Standby' },
  { value: 'inactive', label: 'Inactive' },
];

export default function Monitors() {
  const { canEdit } = useModulePermission('debris');
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';
  const { token } = useAuthStore();

  const { disposalSites, zones, loadDisposalSites, loadZones } = useDebrisStore();
  const { loadTickets, loadLoadTickets } = useDebrisOpsStore();

  const [monitors, setMonitors] = useState<Monitor[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Monitor | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Monitor | null>(null);
  const [form, setForm] = useState<Partial<Monitor>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  const TK = TABLES.debrisMonitors;
  const FL = FIELDS.debrisMonitors;

  const loadMonitors = async () => {
    if (!TK) return;
    setLoading(true);
    try {
      const rows = await fetchTable(TK);
      setMonitors(rows.map((r: any) => ({
        id: r.id || r.Rid,
        Rid: r.Rid || r.id,
        incidentId: String(r.incidentId || ''),
        fullName: String(r.fullName || ''),
        company: String(r.company || ''),
        email: String(r.email || ''),
        phone: String(r.phone || ''),
        role: String(r.role || 'site_monitor'),
        assignedSites: String(r.assignedSites || ''),
        assignedZones: String(r.assignedZones || ''),
        certifications: String(r.certifications || ''),
        status: String(r.status || 'active'),
        notes: String(r.notes || ''),
      })));
    } catch { /* table may not exist yet */ }
    setLoading(false);
  };

  useEffect(() => {
    loadMonitors();
    loadDisposalSites(); loadZones(); loadLoadTickets();
  }, [loadDisposalSites, loadZones, loadLoadTickets]);

  const iMonitors = useMemo(() => incidentId ? monitors.filter(m => m.incidentId === incidentId || !m.incidentId) : monitors, [monitors, incidentId]);
  const iSites = incidentId ? disposalSites : [];
  const iZones = incidentId ? zones : [];
  const iTickets = incidentId ? loadTickets : [];

  const siteSelect = useMemo(() => iSites.map(s => ({ value: String(s.id), label: s.siteName || `#${s.id}` })), [iSites]);
  const zoneSelect = useMemo(() => iZones.map(z => ({ value: String(z.id), label: z.zoneName || z.zoneCode || `#${z.id}` })), [iZones]);

  const filtered = useMemo(() => {
    let list = iMonitors;
    if (statusFilter && statusFilter !== 'all') list = list.filter(m => m.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(m =>
        m.fullName.toLowerCase().includes(q) ||
        m.company.toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q) ||
        m.phone.includes(q),
      );
    }
    return list;
  }, [iMonitors, statusFilter, search]);

  // Stats per monitor from tickets
  const monitorStats = useMemo(() => {
    const stats: Record<string, { verified: number; disputed: number; lastVerified: string }> = {};
    iTickets.forEach(t => {
      if (!t.monitorName) return;
      const key = t.monitorName.toLowerCase().trim();
      if (!stats[key]) stats[key] = { verified: 0, disputed: 0, lastVerified: '' };
      if (t.status === 'verified' || t.status === 'delivered') stats[key].verified++;
      if (t.status === 'disputed') stats[key].disputed++;
      if (t.verifiedAt && t.verifiedAt > (stats[key].lastVerified || '')) stats[key].lastVerified = t.verifiedAt;
    });
    return stats;
  }, [iTickets]);

  const setField = (key: keyof Monitor, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => { setEditTarget(null); setForm({ ...EMPTY }); setModalOpen(true); };
  const openEdit = (m: Monitor) => { setEditTarget(m); setForm({ ...m }); setModalOpen(true); };

  const handleSave = async () => {
    if (!form.fullName?.trim()) { notifications.show({ message: 'Name required', color: 'yellow' }); return; }
    if (!TK || !FL) { notifications.show({ message: 'Monitor table not configured yet — create the Ignatius table first', color: 'orange' }); return; }
    setSaving(true);
    try {
      const fields: Array<[string, string]> = [
        [FL.incidentId, incidentId],
        [FL.fullName, form.fullName?.trim() || ''],
        [FL.company, form.company || ''],
        [FL.email, form.email || ''],
        [FL.phone, form.phone || ''],
        [FL.role, form.role || 'site_monitor'],
        [FL.assignedSites, form.assignedSites || ''],
        [FL.assignedZones, form.assignedZones || ''],
        [FL.certifications, form.certifications || ''],
        [FL.status, form.status || 'active'],
        [FL.notes, form.notes || ''],
      ];
      if (editTarget) {
        await updateRecord(TK, editTarget.id, fields, token ?? undefined);
        notifications.show({ message: 'Monitor updated', color: 'green' });
      } else {
        await createRecord(TK, fields, token ?? undefined);
        notifications.show({ message: 'Monitor added', color: 'green' });
      }
      await loadMonitors();
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget || !TK) return;
    setSaving(true);
    try {
      await deleteRecord(TK, deleteTarget.id);
      notifications.show({ message: `${deleteTarget.fullName} removed`, color: 'gray' });
      setDeleteTarget(null);
      await loadMonitors();
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const siteName = (id: string) => disposalSites.find(s => String(s.id) === id)?.siteName ?? id;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3} c="black">Monitors</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} loading={loading} onClick={loadMonitors}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Monitor</Button>}
        </Group>
      </Group>

      {/* Stats */}
      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid var(--color-primary-700)' }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Total Monitors</Text>
          <Text size="xl" fw={800}>{iMonitors.length}</Text>
        </Paper>
        <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #16a34a' }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Active</Text>
          <Text size="xl" fw={800}>{iMonitors.filter(m => m.status === 'active').length}</Text>
        </Paper>
        <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #d97706' }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Tickets Verified</Text>
          <Text size="xl" fw={800}>{iTickets.filter(t => t.status === 'verified' || t.status === 'delivered').length}</Text>
        </Paper>
        <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #dc2626' }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Disputes</Text>
          <Text size="xl" fw={800}>{iTickets.filter(t => t.status === 'disputed').length}</Text>
        </Paper>
      </SimpleGrid>

      {/* Filters */}
      <Group gap="sm">
        <TextInput placeholder="Search name, email, phone..." leftSection={<IconSearch size={14} />}
          value={search} onChange={e => setSearch(e.currentTarget.value)} size="xs" style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter ?? 'all'} onChange={v => setStatusFilter(v)} size="xs" w={160} />
      </Group>

      <Text size="xs" c="dimmed">{filtered.length} monitor{filtered.length !== 1 ? 's' : ''}</Text>

      {/* Monitor list */}
      {filtered.length === 0 ? (
        <Paper p="xl" radius="md" withBorder style={{ textAlign: 'center' }}>
          <IconUser size={48} color="#ccc" />
          <Text c="dimmed" mt="sm">{loading ? 'Loading...' : 'No monitors found. Add one to get started.'}</Text>
        </Paper>
      ) : (
        <Stack gap="xs">
          {filtered.map(m => {
            const stats = monitorStats[m.fullName.toLowerCase().trim()];
            return (
              <Paper key={m.id} p="md" radius="md" withBorder>
                <Group justify="space-between">
                  <Group gap="md">
                    <div>
                      <Group gap={8}>
                        <Text fw={600}>{m.fullName}</Text>
                        <Badge size="xs" color={m.status === 'active' ? 'green' : m.status === 'standby' ? 'yellow' : 'gray'}>
                          {m.status}
                        </Badge>
                        <Badge size="xs" variant="light">
                          {ROLE_OPTIONS.find(r => r.value === m.role)?.label ?? m.role}
                        </Badge>
                      </Group>
                      <Group gap="md" mt={4}>
                        {m.company && <Text size="xs" fw={500}>{m.company}</Text>}
                        {m.email && <Text size="xs" c="dimmed">{m.email}</Text>}
                        {m.phone && <Text size="xs" c="dimmed">{formatPhone(m.phone)}</Text>}
                        {m.assignedSites && (
                          <Text size="xs" c="dimmed">
                            Sites: {m.assignedSites.split(',').map(id => siteName(id.trim())).join(', ')}
                          </Text>
                        )}
                      </Group>
                    </div>
                  </Group>
                  <Group gap="md">
                    {stats && (
                      <Group gap="xs">
                        <Badge size="sm" color="green" variant="light" leftSection={<IconShieldCheck size={10} />}>
                          {stats.verified} verified
                        </Badge>
                        {stats.disputed > 0 && (
                          <Badge size="sm" color="red" variant="light">{stats.disputed} disputed</Badge>
                        )}
                      </Group>
                    )}
                    {canEdit && (
                      <Group gap={4}>
                        <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(m)}>
                          <IconPencil size={14} />
                        </ActionIcon>
                        <ActionIcon size="sm" variant="subtle" color="red" onClick={() => setDeleteTarget(m)}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    )}
                  </Group>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Create/Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Monitor' : 'Add Monitor'} centered size="md">
        <Stack gap="sm">
          <SimpleGrid cols={2}>
            <TextInput label="Full Name" required value={form.fullName ?? ''}
              onChange={e => setField('fullName', e.currentTarget.value)} placeholder="e.g. Jane Smith" />
            <TextInput label="Company" value={form.company ?? ''}
              onChange={e => setField('company', e.currentTarget.value)} placeholder="e.g. AshBritt Environmental" />
          </SimpleGrid>
          <SimpleGrid cols={2}>
            <TextInput label="Email" value={form.email ?? ''}
              onChange={e => setField('email', e.currentTarget.value)} placeholder="jane@example.com" />
            <TextInput label="Phone" value={form.phone ?? ''}
              onChange={e => setField('phone', e.currentTarget.value)} placeholder="(555) 123-4567" />
          </SimpleGrid>
          <SimpleGrid cols={2}>
            <Select label="Role" data={ROLE_OPTIONS} value={form.role || 'site_monitor'}
              onChange={v => setField('role', v ?? 'site_monitor')} />
            <Select label="Status" data={STATUS_OPTIONS} value={form.status || 'active'}
              onChange={v => setField('status', v ?? 'active')} />
          </SimpleGrid>
          <Select label="Assigned Sites" data={siteSelect} value={form.assignedSites || null}
            onChange={v => setField('assignedSites', v ?? '')} searchable clearable
            placeholder="Select disposal site..." />
          <Select label="Assigned Zones" data={zoneSelect} value={form.assignedZones || null}
            onChange={v => setField('assignedZones', v ?? '')} searchable clearable
            placeholder="Select zone..." />
          <TextInput label="Certifications" value={form.certifications ?? ''}
            onChange={e => setField('certifications', e.currentTarget.value)}
            placeholder="e.g. FEMA IS-200, Hazmat Awareness" />
          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.currentTarget.value)} autosize minRows={2} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>{editTarget ? 'Save' : 'Add Monitor'}</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Remove Monitor" centered size="sm">
        <Stack>
          <Text size="sm">Remove <strong>{deleteTarget?.fullName}</strong>? This won't affect existing ticket records.</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Remove</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
